import { useMemo, useCallback } from 'react';
import type Handsontable from 'handsontable';
import {
  useRegisterTableActions,
  type TableActionsState,
} from '../../../contexts/ToolbarContext';
import type { UnifiedChangeTracker } from '../tracking';

interface UseTrackedTableActionsOptions {
  tableId: string;
  enableTracking: boolean;
  enableLocalToolbar: boolean;
  changeVersion: number;
  tracker: UnifiedChangeTracker;
  restoreBaselineValues: () => void;
  onClear?: () => void;
  hotTableRef: React.RefObject<{ getHotInstance?: () => Handsontable | null } | null>;
}

/**
 * Регистрирует действия таблицы (undo/redo/clear/changedCells)
 * в ToolbarContext для отображения в тулбаре.
 */
export function useTrackedTableActions({
  tableId,
  enableTracking,
  enableLocalToolbar,
  changeVersion,
  tracker,
  restoreBaselineValues,
  onClear,
  hotTableRef,
}: UseTrackedTableActionsOptions) {
  const handleClear = useCallback(() => {
    restoreBaselineValues();
    onClear?.();
  }, [restoreBaselineValues, onClear]);

  const handleRedo = useCallback(() => {
    hotTableRef.current?.getHotInstance?.()?.redo();
  }, [hotTableRef]);

  const tableActions = useMemo<TableActionsState>(() => {
    if (!enableTracking || !enableLocalToolbar) return {};
    void changeVersion;
    return {
      onUndo: handleClear,
      onRedo: handleRedo,
      onClear: handleClear,
      changedCells: tracker.getChangedCells(),
    };
  }, [enableTracking, enableLocalToolbar, changeVersion, handleClear, handleRedo, tracker]);

  useRegisterTableActions(tableId, tableActions);

  return { handleClear, handleRedo };
}
